import { useLayoutEffect, useRef, useCallback, useMemo, RefObject } from 'react';

export type StickyColumnsConfig = number | Record<string, number>;

interface BreakpointEntry {
  minWidth: number,
  count: number
}

function normalizeConfig(config: StickyColumnsConfig): BreakpointEntry[] {
  const map = typeof config === 'number' ? { '0': config } : config;

  return Object.entries(map)
    .map(([key, count]) => ({
      minWidth: parseInt(key, 10),
      count,
    }))
    .sort((a, b) => b.minWidth - a.minWidth);
}

function getActiveStickyCount(
  entries: BreakpointEntry[],
  mediaQueries: Map<number, MediaQueryList>
): number {
  for (const entry of entries) {
    if (entry.minWidth === 0) return entry.count;
    const mq = mediaQueries.get(entry.minWidth);
    if (mq?.matches) return entry.count;
  }
  return 0;
}

export function useStickyColumns(
  containerRef: RefObject<HTMLDivElement>,
  config?: StickyColumnsConfig,
  isLoading?: boolean
) {
  const entries = useMemo(
    () => (config ? normalizeConfig(config) : null),
    [config]
  );

  const maxStickyCount = useMemo(
    () => (entries ? Math.max(...entries.map((e) => e.count)) : 0),
    [entries]
  );

  const prevStickyCountRef = useRef<number>(0);

  const applyStickyOffsets = useCallback(
    (mediaQueries: Map<number, MediaQueryList>) => {
      const container = containerRef.current;
      if (!container) return;

      const headerRow = container.querySelector('thead tr');
      if (!headerRow) return;

      const headerCells = headerRow.children;
      if (!headerCells.length) return;

      const bodyRows = container.querySelectorAll('tbody tr');
      const count = Math.min(
        getActiveStickyCount(entries, mediaQueries),
        headerCells.length
      );

      const widths: number[] = [];
      for (let i = 0; i < count; i++) {
        widths.push(headerCells[i].getBoundingClientRect().width);
      }

      const offsets: number[] = [];
      let cumulative = 0;
      for (let i = 0; i < count; i++) {
        offsets.push(cumulative);
        cumulative += widths[i];
      }

      // Clear sticky styles on columns that are no longer sticky
      const prevCount = prevStickyCountRef.current;
      const maxClearIndex = Math.max(prevCount, count);

      for (let i = count; i < maxClearIndex && i < headerCells.length; i++) {
        const headerCell = headerCells[i] as HTMLElement;
        headerCell.style.position = '';
        headerCell.style.left = '';
        headerCell.style.zIndex = '';

        bodyRows.forEach((row) => {
          const cell = row.children[i] as HTMLElement;
          cell.style.position = '';
          cell.style.left = '';
          cell.style.zIndex = '';
        });
      }

      for (let i = 0; i < count; i++) {
        const headerCell = headerCells[i] as HTMLElement;
        headerCell.style.position = 'sticky';
        headerCell.style.left = `${offsets[i]}px`;
        headerCell.style.zIndex = '1001';

        bodyRows.forEach((row) => {
          const cell = row.children[i] as HTMLElement;
          cell.style.position = 'sticky';
          cell.style.left = `${offsets[i]}px`;
          cell.style.zIndex = '1000';
        });
      }

      prevStickyCountRef.current = count;
    },
    [containerRef, entries]
  );

  useLayoutEffect(() => {
    if (!entries || !containerRef.current) return;

    const container = containerRef.current;

    // Create matchMedia queries for each breakpoint (except 0)
    const mediaQueries = new Map<number, MediaQueryList>();
    for (const entry of entries) {
      if (entry.minWidth > 0 && typeof window.matchMedia === 'function') {
        mediaQueries.set(
          entry.minWidth,
          window.matchMedia(`(min-width: ${entry.minWidth}px)`)
        );
      }
    }

    const apply = () => applyStickyOffsets(mediaQueries);
    apply();

    // Listen for breakpoint changes
    const handleChange = () => apply();
    mediaQueries.forEach((mq) => {
      mq.addEventListener('change', handleChange);
    });

    // Observe header resizes
    const observer = new ResizeObserver(apply);
    const headerRow = container.querySelector('thead tr');
    if (headerRow) {
      const headers = headerRow.children;
      for (let i = 0; i < Math.min(maxStickyCount, headers.length); i++) {
        observer.observe(headers[i]);
      }
    }

    return () => {
      observer.disconnect();
      mediaQueries.forEach((mq) => {
        mq.removeEventListener('change', handleChange);
      });
    };
  }, [entries, maxStickyCount, applyStickyOffsets, containerRef, isLoading]);
}
